import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getoffersbybrand,
  updateBrandOfferStatus,
  updateOfferFeaturedStatus,
} from "../../apiService/loyalty.service";
import useLoyaltyOffers from "./hooks/useLoyaltyOffers";
import Layout from "../../components/Layout/Layout";
import FeaturedToggle from "./components/FeaturedToggle";
import BrandStatusToggle from "./components/BrandStatusToggle";
import ViewImageModal from "./components/ViewImageModal";

const normalizeOfferFromApi = (offer, index) => {
  const normalizedStatus =
    typeof offer?.status === "string"
      ? offer.status.toLowerCase()
      : offer?.status === 1 || offer?.status === true || offer?.is_active === true
        ? "active"
        : "inactive";

  return {
    ...offer,
    id: offer?.id ?? offer?._id ?? offer?.offer_id ?? `offer-${index}`,
    brand_id: offer?.brand_id ?? offer?.brandId ?? null,
    title_en:
      offer?.title_en ??
      offer?.offer_title_en ??
      offer?.offer_name_en ??
      offer?.name ??
      offer?.title ??
      `Offer ${index + 1}`,
    tagline_en: offer?.tagline_en ?? offer?.offer_tagline_en ?? offer?.tagline ?? "",
    offer_banner: offer?.offer_banner ?? offer?.banner_url ?? offer?.banner ?? "",
    save_amount: offer?.save_amount ?? offer?.saved_amount ?? offer?.discount_amount ?? "",
    redemption_price: offer?.redemption_price ?? offer?.redemptionPrice ?? offer?.price ?? "",
    is_featured: Boolean(offer?.is_featured ?? offer?.featured ?? offer?.featured_flag ?? false),
    featured: Boolean(offer?.featured ?? offer?.is_featured ?? offer?.featured_flag ?? false),
    status: normalizedStatus,
  };
};

export default function BrandOffers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { brandId } = useParams();
  const [brandMeta, setBrandMeta] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewImage, setPreviewImage] = useState({
    isOpen: false,
    imageUrl: "",
    title: "",
  });
  const { offers, fetchOffersByBrand, setOffers } = useLoyaltyOffers();

  const handleOfferFeaturedToggle = async (offer) => {
    if (!offer?.id) return;

    const previousStatus = Boolean(offer.featured ?? offer.is_featured ?? false);
    const nextStatus = !previousStatus;

    setOffers((prev) =>
      prev.map((item) =>
        item.id === offer.id ? { ...item, featured: nextStatus, is_featured: nextStatus } : item
      )
    );

    try {
      await updateOfferFeaturedStatus(offer.id);
    } catch (error) {
      setOffers((prev) =>
        prev.map((item) =>
          item.id === offer.id
            ? { ...item, featured: previousStatus, is_featured: previousStatus }
            : item
        )
      );
      console.error("Failed to update offer featured status:", error);
    }
  };

  const handleOfferStatusToggle = async (offer) => {
    if (!offer?.id) return;

    const previousStatus = offer.status === "active" ? "active" : "inactive";
    const nextStatus = previousStatus === "active" ? "inactive" : "active";

    setOffers((prev) =>
      prev.map((item) =>
        item.id === offer.id ? { ...item, status: nextStatus } : item
      )
    );

    try {
      await updateBrandOfferStatus(offer.id);
    } catch (error) {
      setOffers((prev) =>
        prev.map((item) =>
          item.id === offer.id ? { ...item, status: previousStatus } : item
        )
      );
      console.error("Failed to update offer status:", error);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return;

    let cancelled = false;

    const loadOffers = async () => {
      try {
        const response = await getoffersbybrand(brandId, currentPage);
        if (cancelled) return;

        const offerData = Array.isArray(response?.dataList)
          ? response.dataList
          : Array.isArray(response?.offers)
            ? response.offers
            : [];
        const brandData = response?.brand || null;

        setBrandMeta(brandData);
        setCurrentPage(Number(response?.currentPage || currentPage));
        setTotalPages(Number(response?.totalPages || 1));

        const normalizedOffers = offerData.map((offer, index) => normalizeOfferFromApi(offer, index));
        fetchOffersByBrand(brandId, normalizedOffers);
      } catch (error) {
        if (!cancelled) {
          setBrandMeta(null);
          fetchOffersByBrand(brandId, []);
        }
      }
    };

    loadOffers();

    return () => {
      cancelled = true;
    };
  }, [brandId, currentPage, fetchOffersByBrand]);

  const handlePreviewImage = (imageUrl, title = "Image preview") => {
    setPreviewImage({
      isOpen: Boolean(imageUrl),
      imageUrl: imageUrl || "",
      title,
    });
  };

  const handlePageChange = (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), totalPages || 1);
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  };

  const brandName =
    brandMeta?.brand_name_en ||
    brandMeta?.brand_name ||
    brandMeta?.title_en ||
    location.state?.brandName ||
    `Brand ${brandId || ""}`.trim();

  return (
    <Layout>
      <main>
        <ViewImageModal
          isOpen={previewImage.isOpen}
          imageUrl={previewImage.imageUrl}
          title={previewImage.title}
          onClose={() => handlePreviewImage("", "")}
        />

    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Brand</p>
          <h1 className="text-3xl font-bold text-gray-900">{brandName}</h1>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-white"
            onClick={() => navigate(`/services/loyalty-offers/${brandId}/offers/add`, { state: { brandId } })}
          >
            Add Offer
          </button>
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
            onClick={() => navigate("/services/loyalty-offers")}
          >
            Back to Brands
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {offers.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 font-semibold">Offer Banner</th>
                  <th className="px-4 py-3 font-semibold">Brand ID</th>
                  <th className="px-4 py-3 font-semibold">Offer Title (EN)</th>
                  <th className="px-4 py-3 font-semibold">Offer Tagline (EN)</th>
                  <th className="px-4 py-3 font-semibold">Save Amount</th>
                  <th className="px-4 py-3 font-semibold">Redemption Price</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Featured</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {offers.map((offer) => (
                  <tr key={offer.id} className="align-middle text-gray-700">
                    <td className="px-4 py-3">
                      {offer.offer_banner ? (
                        <button
                          type="button"
                          onClick={() => handlePreviewImage(offer.offer_banner, offer.title_en || "Offer banner")}
                          className="inline-flex cursor-pointer rounded-md border border-gray-200 bg-white p-1 transition hover:border-cyan-300 hover:shadow-sm"
                        >
                          <img
                            src={offer.offer_banner}
                            alt={offer.title_en || "Offer banner"}
                            className="h-12 w-20 rounded-md object-cover"
                          />
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">{offer.brand_id ?? brandId}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{offer.title_en || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{offer.tagline_en || offer.offer_tagline_en || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{offer.save_amount ?? offer.saved_amount ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{offer.redemption_price ?? "—"}</td>
                    <td className="px-4 py-3">
                      <BrandStatusToggle
                        programTitle={offer.title_en || "Offer"}
                        status={offer.status}
                        disabled={false}
                        isUpdating={false}
                        onToggle={() => handleOfferStatusToggle(offer)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <FeaturedToggle
                        brandTitle={offer.title_en}
                        isFeatured={Boolean(offer.featured || offer.is_featured)}
                        disabled={false}
                        isUpdating={false}
                        onToggle={() => handleOfferFeaturedToggle(offer)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700"
                          onClick={() => navigate(`/services/loyalty-offers/${brandId}/offers/edit/${offer.id}`, { state: { offer } })}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="rounded-lg bg-cyan-500 px-3 py-2 text-xs font-semibold text-white"
                          onClick={() =>
                            navigate(
                              `/services/loyalty-offers/${brandId}/offers/${offer.id}/coupons`
                            )
                          }
                        >
                          Coupons
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-slate-50 p-10 text-center text-gray-500">
            No offers found for this brand.
          </div>
        )}

        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </button>

          <span className="text-sm font-medium text-gray-700">
            Page {currentPage} of {totalPages || 1}
          </span>

          <button
            type="button"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || totalPages <= 1}
          >
            Next
          </button>
        </div>
      </div>
    </div>
    </main>
    </Layout>
  );
}
